/**
 * Iter336 · Quick Action Panel — surfaces the 3 fastest actions the
 * sales rep can take when a pin is tapped on Leads Map: start email
 * sequence, add to pipeline, generate AI pitch.
 *
 * Renders as a mobile bottom sheet at <md and as an anchored card on
 * desktop. Uses the shared BottomSheet primitive.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Mail, Kanban, Sparkles, Copy, Loader2, X, ChevronRight, Check,
} from "lucide-react";
import BottomSheet, { useIsMobile } from "./BottomSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickActionPanel({ lead, isPt, onClose }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [pitch, setPitch] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [pipelineDone, setPipelineDone] = useState(false);
  const [pipelineLoading, setPipelineLoading] = useState(false);

  if (!lead) return null;

  const startSequence = () => {
    navigate(`/campaigns?lead=${encodeURIComponent(lead.id)}&company=${
      encodeURIComponent(lead.company_name || lead.name || "")}`);
    onClose?.();
  };

  const addToPipeline = async () => {
    setPipelineLoading(true);
    try {
      await axios.post(`${API}/leads-map/add-to-pipeline`,
                        { lead_id: lead.id, stage: "NEW" });
      setPipelineDone(true);
      toast.success(isPt ? "Adicionado ao Kanban" : "Added to Kanban",
                     { action: {
                         label: isPt ? "Ver" : "See",
                         onClick: () => navigate(`/kanban?q=${
                             encodeURIComponent(lead.company_name || lead.name || "")}`),
                       }});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setPipelineLoading(false); }
  };

  const runPitch = async () => {
    setPitchLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads-map/pitch`,
                                          { lead_id: lead.id, lang: isPt ? "pt" : "en" });
      setPitch(data.pitch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI pitch failed");
    } finally { setPitchLoading(false); }
  };

  const copyPitch = async () => {
    if (!pitch) return;
    const txt = [
      `Subject: ${pitch.subject}`, "",
      pitch.opener, "", pitch.body, "", pitch.cta, "",
      pitch.ps ? `PS: ${pitch.ps}` : "",
    ].join("\n").trim();
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(isPt ? "Copiado para área de transferência" : "Copied");
    } catch { toast.error("Copy failed"); }
  };

  const body = (
    <div className="p-4 space-y-3" data-testid="lm-quickaction-body">
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)]">
        {isPt ? "Ações rápidas" : "Quick actions"}
      </div>
      <button type="button" onClick={startSequence}
        data-testid="lm-qa-sequence"
        className="w-full h-12 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[13px] font-bold inline-flex items-center justify-center gap-2">
        <Mail className="w-4 h-4" />
        {isPt ? "Iniciar sequência de e-mail" : "Start email sequence"}
        <ChevronRight className="w-3.5 h-3.5 ml-auto" />
      </button>
      <button type="button" onClick={addToPipeline}
        disabled={pipelineLoading || pipelineDone}
        data-testid="lm-qa-pipeline"
        className={`w-full h-12 rounded-lg text-[13px] font-bold inline-flex items-center justify-center gap-2 ${
          pipelineDone
            ? "bg-emerald-500 text-white"
            : "bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233]"} disabled:opacity-70`}>
        {pipelineLoading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : pipelineDone
          ? <Check className="w-4 h-4" />
          : <Kanban className="w-4 h-4" />}
        {pipelineDone
          ? (isPt ? "Adicionado ao Kanban" : "Added to Kanban")
          : (isPt ? "Adicionar ao pipeline" : "Add to sales pipeline")}
        <ChevronRight className="w-3.5 h-3.5 ml-auto" />
      </button>
      <button type="button" onClick={runPitch}
        disabled={pitchLoading}
        data-testid="lm-qa-pitch"
        className="w-full h-12 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-70">
        {pitchLoading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Sparkles className="w-4 h-4" />}
        {isPt ? "Disparar campanha de IA" : "Generate AI pitch"}
        <ChevronRight className="w-3.5 h-3.5 ml-auto" />
      </button>

      {pitch && (
        <div className="mt-4 border-t border-[var(--al-border)] pt-3 space-y-2"
             data-testid="lm-qa-pitch-result">
          <div className="flex items-center justify-between">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
              Claude Sonnet 5
            </span>
            <button type="button" onClick={copyPitch}
              data-testid="lm-qa-pitch-copy"
              className="h-7 px-2.5 rounded bg-[var(--al-bg)] text-[11px] font-bold inline-flex items-center gap-1 hover:bg-[#1B3A8A] hover:text-white">
              <Copy className="w-3 h-3" />
              {isPt ? "Copiar" : "Copy"}
            </button>
          </div>
          <div className="text-[11px] font-bold text-[var(--al-text)]">
            {isPt ? "Assunto: " : "Subject: "}{pitch.subject}
          </div>
          <div className="text-[12px] leading-relaxed text-[var(--al-text)] whitespace-pre-line">
            {[pitch.opener, "", pitch.body, "", pitch.cta,
              pitch.ps ? `\nPS: ${pitch.ps}` : ""].join("\n")}
          </div>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={true}
        onClose={onClose}
        title={lead.sector || (isPt ? "Ação rápida" : "Quick action")}
        subtitle={lead.company_name || lead.name}
        testid="lm-quickaction-sheet"
        height="88vh"
      >
        {body}
      </BottomSheet>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="lm-quickaction-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="rounded-xl w-full max-w-md border border-[var(--al-border)] shadow-2xl overflow-hidden"
           style={{ backgroundColor: "var(--al-card-bg, #FFFFFF)" }}>
        <div className="flex items-start justify-between px-4 py-3 border-b border-[var(--al-border)]">
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
              {lead.sector || (isPt ? "Lead" : "Lead")}
            </div>
            <div className="text-[14px] font-semibold text-[var(--al-text)] truncate">
              {lead.company_name || lead.name}
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="lm-qa-close"
            className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[var(--al-text-muted)] hover:bg-[var(--al-bg)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}
